import { styled } from '@mui/material/styles';
import { Card as MuiCard, CardHeader, CardContent, CardActions } from '@mui/material';
import { CardVariant, CardPadding } from './Card.types';
import { designTokens } from '../../tokens';

/**
 * Padding styles
 */
const getPaddingStyles = (padding: CardPadding) => {
  switch (padding) {
    case 'none':
      return {
        padding: 0,
      };
    case 'sm':
      return {
        padding: designTokens.spacing[2],
      };
    case 'md':
      return {
        padding: designTokens.spacing[3],
      };
    case 'lg':
      return {
        padding: designTokens.spacing[4],
      };
    default:
      return {};
  }
};

/**
 * Variant styles
 */
const getVariantStyles = (variant: CardVariant) => {
  const tokens = designTokens.colors;

  switch (variant) {
    case 'elevated':
      return {
        boxShadow: designTokens.shadows.md,
        border: 'none',
        backgroundColor: tokens.background.paper,
        '&:hover': {
          boxShadow: designTokens.shadows.lg,
        },
      };

    case 'outlined':
      return {
        boxShadow: 'none',
        border: `1px solid ${tokens.border.light}`,
        backgroundColor: tokens.background.paper,
        '&:hover': {
          borderColor: tokens.border.medium,
        },
      };

    case 'flat':
      return {
        boxShadow: 'none',
        border: 'none',
        backgroundColor: tokens.background.default,
      };

    default:
      return {};
  }
};

/**
 * Styled Card component
 */
export const StyledCard = styled(MuiCard, {
  shouldForwardProp: (prop) =>
    !['cardVariant', 'clickable', 'fullHeight'].includes(prop as string),
})<{
  cardVariant: CardVariant;
  clickable?: boolean;
  fullHeight?: boolean;
}>(({ cardVariant, clickable, fullHeight }) => ({
  ...getVariantStyles(cardVariant),

  borderRadius: designTokens.borderRadius.lg,
  transition: `all ${designTokens.transitions.duration.fast} ${designTokens.transitions.timing.easeInOut}`,
  height: fullHeight ? '100%' : 'auto',
  display: 'flex',
  flexDirection: 'column',

  ...(clickable && {
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      ...(cardVariant === 'elevated' && {
        boxShadow: designTokens.shadows.xl,
      }),
      ...(cardVariant === 'outlined' && {
        borderColor: designTokens.colors.primary[500],
      }),
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  }),

  '&.Mui-disabled': {
    opacity: 0.6,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
}));

/**
 * Styled CardHeader
 */
export const StyledCardHeader = styled(CardHeader)(() => ({
  fontFamily: designTokens.typography.fontFamily.primary,

  '& .MuiCardHeader-title': {
    fontSize: designTokens.typography.fontSize.lg,
    fontWeight: designTokens.typography.fontWeight.semibold,
    color: designTokens.colors.neutral[900],
  },

  '& .MuiCardHeader-subheader': {
    fontSize: designTokens.typography.fontSize.sm,
    fontWeight: designTokens.typography.fontWeight.regular,
    color: designTokens.colors.neutral[600],
    marginTop: designTokens.spacing[1],
  },
}));

/**
 * Styled CardContent
 */
export const StyledCardContent = styled(CardContent, {
  shouldForwardProp: (prop) => prop !== 'cardPadding',
})<{ cardPadding: CardPadding }>(({ cardPadding }) => ({
  ...getPaddingStyles(cardPadding),

  flex: 1,
  fontFamily: designTokens.typography.fontFamily.primary,
  fontSize: designTokens.typography.fontSize.sm,
  color: designTokens.colors.neutral[700],

  '&:last-child': {
    paddingBottom: cardPadding === 'none' ? 0 : undefined,
  },
}));

/**
 * Styled CardActions (footer)
 */
export const StyledCardActions = styled(CardActions)(() => ({
  padding: `${designTokens.spacing[2]} ${designTokens.spacing[3]}`,
  borderTop: `1px solid ${designTokens.colors.border.light}`,
  gap: designTokens.spacing[2],
}));
